<script setup lang="ts">
import {computed} from 'vue';
import {pagesPath} from '@/ts/router/pages-path';
import type {ProgramCatalogue} from '@/ts/types/component/today-page';

/**
 * Stands in for a catalogue the user has no programs in, inviting them to browse
 * it. Unlike a program row this has no surface of its own — the design leaves it
 * transparent against the tile.
 *
 * Its sub-copy has its own keys, though today they read the same as the tile's
 * empty state blurbs: the design had two texts, and product settled on one.
 */
const props = defineProps<{
  catalogue: ProgramCatalogue;
}>();

/** The invitation was taken (IPD-2974), carrying which catalogue rather than where it leads. */
const emit = defineEmits<{browse: [catalogue: ProgramCatalogue]}>();

// Literal keys so `no-missing-keys` can still see them; see the catalogue block.
const isMedical = computed(() => props.catalogue === 'medical');
const titleKey = computed(() => (isMedical.value
  ? 'program_tile_discover_medical_title'
  : 'program_tile_discover_prevention_title'));
const subKey = computed(() => (isMedical.value
  ? 'program_tile_discover_medical_sub'
  : 'program_tile_discover_prevention_sub'));
const ctaKey = computed(() => (isMedical.value
  ? 'program_tile_browse_medical_short'
  : 'program_tile_browse_prevention_short'));

/** Medical programs live under Paths; prevention ones are browsed from Explore. */
const target = computed(() => (isMedical.value
  ? pagesPath.intervention.interventionsList
  : pagesPath.main.explore));
</script>

<template>
  <router-link class="pd" :to="target" @click="emit('browse', catalogue)">
    <span class="pd-text">
      <span class="pd-title">{{ $t(titleKey) }}</span>
      <span class="pd-sub">{{ $t(subKey) }}</span>
    </span>
    <span class="pd-cta">
      {{ $t(ctaKey) }}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
    </span>
  </router-link>
</template>

<style scoped lang="scss">
.pd {
  display: flex;
  text-decoration: none;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 83px;
  padding: 16px 24px;
  // A dashed outline marks this as a placeholder for the programs that would
  // be here — the one thing that tells it apart from a real row.
  border: 1px dashed var(--pt-outline, rgb(32 40 46 / 22%));
  border-radius: 12px;
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;
  transition:
    background 200ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 200ms cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: color-mix(in srgb, var(--pt-outline-ink, #20282e) 36%, transparent);
    background: color-mix(in srgb, var(--pt-outline-ink, #20282e) 4%, transparent);
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @include focus-visible;
}

.pd-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.pd-title {
  font-family: var(--Font-Secondary-Font);
  font-size: 24px;
  font-weight: 300;
  line-height: 21.6px;
  letter-spacing: -0.6px;
}

.pd-sub {
  font-family: var(--Font-Primary-Font);
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  // The design dims this to 0.6, which lands under the contrast floor for 14px
  // body text. The neutral token reads the same but stays legible, and follows
  // the theme instead of thinning the ink over a dark surface.
  color: var(--Neutral-UI-neutral-ui-70);
}

.pd-cta svg {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

.pd-cta {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  font-family: var(--Font-Primary-Font);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

@container program-tile (max-width: 719px) {
  .pd {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .pd-cta {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
